import type * as React from 'react';
import { getPaginated } from '@Domain/Api';
import documentTypesFetched, {
	type DocumentTypePayload,
	type DocumentTypesFetchedAction,
} from '@Application/Documents/DocumentTypes/Actions/documentTypesFetched';
import documentTypesFetchFailed, {
	type DocumentTypesFetchFailedAction,
} from '@Application/Documents/DocumentTypes/Actions/documentTypesFetchFailed';
import type FetchDocumentTypes from '@Application/Documents/DocumentTypes/Queries/FetchDocumentTypes';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import parameters from '../../../parameters';

type Actions = DocumentTypesFetchedAction | DocumentTypesFetchFailedAction;

// `getPaginated` requires a mutable `results` array (it pushes subsequent pages into it); the
// dispatched action still carries the `readonly` form declared by `DocumentTypesFetchedPayload`.
type PaginatedDocumentTypes = {
	totalRecords: number;
	results: DocumentTypePayload[];
};

/**
 * Walks every page without the `active` filter: a referential of a few dozen rows at most,
 * archived types included, since a document produced under an archived type must keep its label —
 * `activeTypes()` on the resulting list is reserved for choice screens.
 */
export default (query: FetchDocumentTypes) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			// Page indexing starts at 0 server-side, and `getPaginated` only walks every page when
			// called with `page: 0` — its internal guard only catches `page > 1`. `rowsPerPage: 200`
			// is the server's maximum accepted `rows` value.
			const response = await getPaginated<PaginatedDocumentTypes>(
				`${parameters.api.host}/api/accounts/${query.accountId}/document-types`,
				{ page: 0, rowsPerPage: 200 },
			);

			const action = documentTypesFetched(response.results, query.accountId);
			dispatch(action);
			return action;
		} catch (error) {
			const action = documentTypesFetchFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
